import React, { memo, useCallback, useEffect, useRef } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { format, isAfter, isBefore, parse } from 'date-fns';
import { usePaymentContext } from 'features/payment';
import {
  CASH_ACCOUNT_IDENTIFIER,
  CASH_APP_ACCOUNT_IDENTIFIER,
  PAYPAL_ACCOUNT_IDENTIFIER,
  VENMO_ACCOUNT_IDENTIFIER,
} from 'features/payment/constants';
import { CardTypes, IFraudPreventionValues, IPaymentDetails } from 'features/payment/types';
import { splitExpiry } from 'features/payment/utils';
import { ExecutionResult } from 'graphql';
import { IntlFormatters, useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { ICommitOrderMutation, useGetRestaurantsQuery } from 'generated/graphql-gateway';
import { ICommitOrderInput, useCommitOrder } from 'hooks/commit-order';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useCart } from 'hooks/use-cart';
import useErrorModal from 'hooks/use-error-modal';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DAY_PART_SELECTIONS, IValidDayPart } from 'state/menu/main-menu/types';
import { OrderStatus, useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import {
  GraphQLErrorCodes,
  getErrorDialogForGraphQLCode,
  parseGraphQLErrorCodes,
} from 'utils/errors';
import { logger } from 'utils/logger';
import { readableCloseHourToday } from 'utils/restaurant';
import { DAYPART_FORMAT, TWELVE_HOUR_TIME_PARSE_FORMAT } from 'utils/restaurant/constants';
import { routes } from 'utils/routing';

import { CardButton } from './card-button';

type onCommitType = (input: ICommitOrderInput) => Promise<ExecutionResult<ICommitOrderMutation>>;

type onCommitErrorType = (error: any) => void;

const Pickup = Text.withConfig({
  color: Styles.color.accent,
});

interface IHandleCommitOrder {
  onCommit: onCommitType;
  onCommitError: onCommitErrorType;
  payment: {
    clearPaymentDetails(): void;
    paymentDetails: IPaymentDetails;
  };
  skipCoolingPeriod?: boolean;
}
const handleCommitOrder = ({
  onCommit,
  onCommitError,
  payment,
  skipCoolingPeriod = false,
}: IHandleCommitOrder) => {
  const { paymentDetails, clearPaymentDetails } = payment;
  const {
    accountIdentifier,
    creditType,
    fdAccessToken,
    fdNonce,
    fraudPreventionValues = {} as IFraudPreventionValues,
    applePayDetails,
    googlePayDetails,
    paypalDetails,
    venmoDetails,
    cashappDetails,
  } = paymentDetails;

  let commitInput: ICommitOrderInput;

  // TODO refactor: this code should not be much different than non pickup orders but is a landmine when adding new payment methods
  if (accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: CASH_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      payment: {
        cashPayment: true,
        fullName: fraudPreventionValues.fullName,
      },
    };
  } else if (accountIdentifier === PAYPAL_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: PAYPAL_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      paypalDetails,
    };
  } else if (accountIdentifier === VENMO_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: VENMO_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      venmoDetails,
    };
  } else if (accountIdentifier === CASH_APP_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: CASH_APP_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      payment: {
        fullName: '',
        cashAppInput: {
          fdAccessToken: cashappDetails!.fdAccessToken,
          fdNonce: cashappDetails!.fdNonce,
        },
      },
    };
  } else {
    const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

    commitInput = {
      applePayDetails,
      creditType,
      fdAccountId: accountIdentifier,
      fullName: fraudPreventionValues.fullName,
      payment: {
        billingAddress: fraudPreventionValues.billingAddress,
        ccMetadata: {
          ccBin: fraudPreventionValues.ccBin,
          ccExpiryMonth: expiryMonth,
          ccExpiryYear: expiryYear,
          ccLast4: fraudPreventionValues.ccLast4,
        },
        firstDataInput: {
          accountIdentifier,
          fdAccessToken,
          fdNonce,
        },
        fullName: fraudPreventionValues.fullName,
      },
      googlePayDetails,
      skipCoolingPeriod,
    };
  }

  return onCommit(commitInput)
    .then(() => {
      clearPaymentDetails();
    })
    .catch(onCommitError);
};

interface IGetHoursText {
  dayParts: readonly IValidDayPart[];
  formatMessage: IntlFormatters['formatMessage'];
  hours: Pick<IRestaurant, 'driveThruHours' | 'diningRoomHours'>;
  orderedAt: Date;
  serviceMode: ServiceMode;
}

// TODO: RN - locale switching -- can we get rid of this? only used in test files?
// if not, will need to update to accept timeFormatConfig prop to pass to readableCloseHourToday
// e.x const timeFormatConfig = useConfigValue({ key: 'timeFormat' });
export const getHoursText = ({
  dayParts,
  formatMessage,
  hours,
  orderedAt,
  serviceMode,
}: IGetHoursText) => {
  const { startTime, endTime } =
    dayParts.find(dayPart => dayPart.key === DAY_PART_SELECTIONS.BREAKFAST_KEY) || {};
  const breakfastStartTime = startTime ? parse(startTime, DAYPART_FORMAT, new Date()) : null;
  const breakfastEndTime = endTime ? parse(endTime, DAYPART_FORMAT, new Date()) : null;

  const isBreakfastOrder =
    breakfastStartTime && breakfastEndTime
      ? isAfter(orderedAt, breakfastStartTime) && isBefore(orderedAt, breakfastEndTime)
      : false;

  const serviceModeHours =
    serviceMode === ServiceMode.DRIVE_THRU ? hours.driveThruHours : hours.diningRoomHours;

  const pickupTime =
    isBreakfastOrder && breakfastEndTime
      ? format(breakfastEndTime, TWELVE_HOUR_TIME_PARSE_FORMAT)
      : readableCloseHourToday(serviceModeHours);

  // TODO not sure why this is having issues with typescript update, casting to any to fix for now.
  return formatMessage({ id: 'pickUpBefore' }, {
    pickupTime,
    pickup: (str: string) => <Pickup>{str}</Pickup>,
  } as any);
};

const PickupCard = ({
  serverOrder,
  needConfirm,
  setNeedConfirmation,
  setProcessingPayment,
}: {
  confirmed?: boolean;
  serverOrder: IServerOrder;
  needConfirm?: boolean;
  setNeedConfirmation?: (value: boolean) => void;
  setProcessingPayment?: (value: boolean) => void;
}) => {
  const { formatMessage } = useIntl();
  const { onCommitSuccess } = useOrderContext();
  const { emptyCart } = useCart();
  const dismissAndNavigate = useDismissAllAndNavigate();

  const { store: restaurant } = useStoreContext();

  const distanceToStore = useDistance({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const { commitOrder, loading, orderStatus, commitResult } = useCommitOrder({
    rbiOrderId: serverOrder.rbiOrderId,
    distanceToStore,
  });

  const { data: restaurantsData, loading: loadingRestaurant } = useGetRestaurantsQuery({
    variables: {
      input: {
        first: 1,
        coordinates: {
          userLat: serverOrder.cart.storeDetails.latitude,
          userLng: serverOrder.cart.storeDetails.longitude,
          searchRadius: 20,
        },
      },
    },
  });

  const {
    clearPaymentDetails,
    paymentDetails,
    loading: paymentDetailsLoading,
  } = usePaymentContext();
  const onCommitError = (err: any) => {
    throw err;
  };

  const onCommitOrderSkipCooldown = useCallback(() => {
    const payment = {
      clearPaymentDetails,
      paymentDetails,
    };

    handleCommitOrder({
      onCommit: commitOrder,
      onCommitError,
      payment,
      skipCoolingPeriod: true,
    }).then(() => {
      onCommitSuccess(serverOrder);
      return emptyCart();
    });
  }, [clearPaymentDetails, commitOrder, emptyCart, onCommitSuccess, paymentDetails, serverOrder]);

  const onCommitOrder = useCallback(async () => {
    let cleanPaymentDetails = paymentDetails;

    // Cleaning Google / Apple Pay details, edge case fix for Curbside + Reloading prepaid flow
    if (
      paymentDetails.accountIdentifier !== CardTypes.GOOGLE_PAY &&
      paymentDetails.accountIdentifier !== CardTypes.APPLE_PAY
    ) {
      cleanPaymentDetails = {
        ...paymentDetails,
        applePayDetails: undefined,
        googlePayDetails: undefined,
      };
    }

    const payment = {
      clearPaymentDetails,
      paymentDetails: cleanPaymentDetails,
    };

    logger.info({
      message: '[INFO] Order Confirmation - Payment Details',
      paymentDetails: cleanPaymentDetails,
    });

    await handleCommitOrder({ onCommit: commitOrder, onCommitError, payment });
    onCommitSuccess(serverOrder);
    return emptyCart();
  }, [clearPaymentDetails, commitOrder, emptyCart, onCommitSuccess, paymentDetails, serverOrder]);

  const onCancelModal = () => dismissAndNavigate(routes.orders);
  const [OrderCooldownErrorDialog, openOrderCooldownErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
  });

  const orderIsLoading =
    loading || paymentDetailsLoading || orderStatus === OrderStatus.INSERT_REQUESTED;

  const enableCheckoutAnimation = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_ANIMATION);
  const isOrderStatusFailureHandledRef = useRef(false);

  useEffect(() => {
    if (!commitResult.error) {
      return;
    }

    const [parsedGqlError] = parseGraphQLErrorCodes(commitResult.error);
    if (!parsedGqlError) {
      return;
    }

    const mappedErrorCode = getErrorDialogForGraphQLCode(parsedGqlError, formatMessage);
    const isCommitOrderCoolingPeriodError =
      parsedGqlError.errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD;

    if (
      mappedErrorCode &&
      !isOrderStatusFailureHandledRef.current &&
      isCommitOrderCoolingPeriodError
    ) {
      isOrderStatusFailureHandledRef.current = true;
      openOrderCooldownErrorDialog({
        body: formatMessage({ id: 'errorDialogCooldownMessage' }),
        title: formatMessage({ id: 'errorDialogCooldownTitle' }),
        message: formatMessage({ id: 'errorDialogCooldownMessage' }),
        dismissText: formatMessage({ id: 'viewRecentOrders' }),
        confirmationText: formatMessage({ id: 'placeOrder' }),
        onDismiss: () => onCancelModal(),
        onConfirm: () => onCommitOrderSkipCooldown(),
      });
    }
  }, [commitResult, formatMessage, openOrderCooldownErrorDialog]);

  return (
    <>
      <Box testID="pickup-card">
        <VisuallyHidden
          role="alert"
          accessibilityLabel={formatMessage(
            { id: 'pickUpConfirmationScreenReaderInstructions' },
            {
              screenReaderButtonName: 'imHere',
            }
          )}
        />
      </Box>
      {!loadingRestaurant && !!restaurantsData?.restaurants?.nodes[0] && (
        <>
          {needConfirm && (
            <CardButton
              isLoading={orderIsLoading}
              onCommit={() => {
                if (enableCheckoutAnimation && setProcessingPayment) {
                  setProcessingPayment(true);
                }
                if (enableCheckoutAnimation && setNeedConfirmation) {
                  setNeedConfirmation(false);
                }
                onCommitOrder();
              }}
              orderStatus={orderStatus}
              restaurant={restaurantsData?.restaurants?.nodes[0]}
            />
          )}
        </>
      )}

      <OrderCooldownErrorDialog />
    </>
  );
};

export default memo(PickupCard);
